if (!customElements.get('me-agentur-delivery')) {
  class MeAgenturDelivery extends HTMLElement {
    connectedCallback() {
      this.inventory = this.closest('me-agentur-inventory');
      this.message = this.querySelector('[data-me-delivery-message]');
      this.prompt = this.querySelector('[data-me-delivery-prompt]');
      this.timer = this.querySelector('[data-me-delivery-timer]');
      this.generation = (this.generation || 0) + 1;
      this.activeVariant = '';
      this.active = false;
      this.hidden = true;
      if (!this.inventory || !this.message || !this.prompt || !this.timer) return;
      this.observer = new MutationObserver(() => this.syncInventory());
      this.observer.observe(this.inventory, {
        attributes: true, attributeFilter: ['hidden', 'data-variant-id', 'data-inventory-state']
      });
      this.syncInventory();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.stop();
    }

    hasOwnStock() {
      return this.isConnected && this.inventory?.isConnected && !this.inventory.hidden
        && this.inventory.dataset.inventoryState === 'own-stock';
    }

    syncInventory() {
      const variantId = this.inventory?.dataset.variantId || '';
      if (!this.hasOwnStock() || !/^\d+$/.test(variantId)) {
        this.stop();
        return;
      }
      if (!this.active || variantId !== this.activeVariant) {
        this.stop();
        this.active = true;
        this.activeVariant = variantId;
        this.requestPolicy();
      } else if (!this.config && !this.pending && !this.waitTimer) {
        this.requestPolicy();
      }
    }

    clearDisplay() {
      clearInterval(this.tickTimer);
      clearTimeout(this.refreshTimer);
      this.tickTimer = null;
      this.refreshTimer = null;
      this.config = null;
      this.hidden = true;
      if (this.message) this.message.textContent = '';
      if (this.prompt) this.prompt.textContent = '';
      if (this.timer) this.timer.textContent = '';
    }

    stop() {
      this.generation = (this.generation || 0) + 1;
      this.active = false;
      this.activeVariant = '';
      clearTimeout(this.waitTimer);
      this.waitTimer = null;
      this.cancelPending();
      this.clearDisplay();
    }

    cleanupScript(request) {
      clearTimeout(request.timeout);
      const script = request.script;
      if (!script) return;
      script.onerror = null;
      script.onload = null;
      script.remove();
      let callbackName;
      try {
        callbackName = new URL(script.src, window.location.origin).searchParams.get('callback');
      } catch {
        return;
      }
      if (!/^orderDeadlineJsonp\d+_\d+$/.test(callbackName || '')) return;
      const noop = () => {};
      window[callbackName] = noop;
      setTimeout(() => {
        if (window[callbackName] === noop) delete window[callbackName];
      }, 120000);
    }

    cancelPending() {
      if (!this.pending) return;
      this.cleanupScript(this.pending);
      this.pending = null;
    }

    requestPolicy() {
      if (!this.hasOwnStock()) {
        this.stop();
        return;
      }
      const generation = ++this.generation;
      this.cancelPending();
      clearTimeout(this.waitTimer);
      this.waitTimer = null;
      const waitUntil = performance.now() + 30000;
      const attempt = () => {
        this.waitTimer = null;
        if (generation !== this.generation || !this.hasOwnStock()) return;
        const app = window.orderDeadlineAppByEESL;
        const instruction = this.plainText(app?.installConfig?.instruction, 600);
        if (typeof app?.fetchInstallConfig !== 'function' || !instruction) {
          if (performance.now() < waitUntil) this.waitTimer = setTimeout(attempt, 250);
          else this.failPolicy();
          return;
        }
        this.fetchPolicy(app, instruction, generation);
      };
      attempt();
    }

    failPolicy() {
      this.cancelPending();
      this.clearDisplay();
      this.dataset.deliveryStatus = 'unavailable';
    }

    fetchPolicy(app, instruction, generation) {
      const request = { script: null, timeout: null };
      this.pending = request;
      const validRequest = () => this.pending === request && generation === this.generation
        && this.hasOwnStock();
      const fail = () => {
        if (validRequest()) this.failPolicy();
        else this.cleanupScript(request);
      };
      request.timeout = setTimeout(fail, 8000);
      try {
        const script = app.fetchInstallConfig(
          this.dataset.referenceProductId, this.dataset.referenceVariantId,
          'https:', '', '', 1, (config) => {
            if (!validRequest()) return;
            this.cleanupScript(request);
            this.pending = null;
            const validated = this.validatePolicy(config, instruction);
            if (!validated) {
              this.failPolicy();
              return;
            }
            this.clearDisplay();
            this.config = validated;
            this.receivedAt = performance.now();
            this.dataset.deliveryStatus = 'verified';
            this.message.textContent = validated.message;
            this.prompt.textContent = validated.prompt;
            this.hidden = false;
            this.tick();
            if (this.config) {
              this.tickTimer = setInterval(() => this.tick(), 1000);
              this.refreshTimer = setTimeout(() => this.requestPolicy(), 300000);
            }
          }
        );
        request.script = script;
        if (!script || typeof script.remove !== 'function') throw new Error('Invalid policy response');
        if (this.pending !== request) {
          this.cleanupScript(request);
          return;
        }
        const nativeError = script.onerror;
        const nativeLoad = script.onload;
        script.onerror = (event) => {
          if (typeof nativeError === 'function') nativeError.call(script, event);
          fail();
        };
        script.onload = (event) => {
          if (typeof nativeLoad === 'function') nativeLoad.call(script, event);
          if (this.pending === request) fail();
        };
      } catch {
        fail();
      }
    }

    plainText(value, maxLength) {
      if (typeof value !== 'string' || !value.trim() || value.length > maxLength
        || /[<>\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(value)) return null;
      return value.replace(/\s+/g, ' ').trim();
    }

    validatePolicy(config, instruction) {
      if (!config || String(config.id) !== this.dataset.referenceProductId
        || String(config.variantId) !== this.dataset.referenceVariantId
        || config.active !== true || config.cancelTimerInit !== false
        || config.nextAvailableEnabled !== false || config.hideTimerElements === true
        || config.type !== 'textual') return null;
      const message = this.plainText(config.message, 1000);
      const prompt = this.plainText(config.prompt, 300);
      if (!message || !prompt || /yamaha/i.test(message)
        || this.plainText(config.instruction, 600) !== instruction) return null;
      const values = ['nowTimestamp', 'deadlineTimestamp', 'deliveryTimestamp', 'deliveryRangeTimestamp'];
      if (values.some((key) => !Number.isSafeInteger(config[key]) || config[key] <= 0)
        || config.deadlineTimestamp <= config.nowTimestamp
        || config.deliveryTimestamp < config.deadlineTimestamp
        || config.deliveryRangeTimestamp < config.deliveryTimestamp) return null;
      if (!['', 'always', 'within-1-hour'].includes(config.showSecondsWhen)) return null;
      const result = { message, prompt, showSecondsWhen: config.showSecondsWhen };
      values.forEach((key) => { result[key] = config[key]; });
      for (const key of ['dayText', 'daysText', 'hourText', 'hoursText', 'minuteText', 'minutesText', 'secondText', 'secondsText']) {
        result[key] = this.plainText(config[key], 50);
        if (!result[key]) return null;
      }
      return result;
    }

    tick() {
      if (!this.hasOwnStock()) {
        this.stop();
        return;
      }
      if (!this.config) return;
      const config = this.config;
      const remaining = config.deadlineTimestamp - config.nowTimestamp - (performance.now() - this.receivedAt);
      if (remaining <= 0) {
        this.clearDisplay();
        this.requestPolicy();
        return;
      }
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor(remaining / 3600000 % 24);
      const minutes = Math.floor(remaining / 60000 % 60);
      const seconds = Math.floor(remaining / 1000 % 60);
      const parts = [];
      const add = (count, singular, plural, format = String) => {
        parts.push(`${format(count)} ${config[count === 1 ? singular : plural]}`);
      };
      if (days > 0) add(days, 'dayText', 'daysText');
      if (hours > 0) add(hours, 'hourText', 'hoursText');
      if (minutes > 0) add(minutes, 'minuteText', 'minutesText');
      const showSeconds = config.showSecondsWhen === 'always'
        || remaining < (config.showSecondsWhen === 'within-1-hour' ? 3600000 : 7200000);
      if (showSeconds) add(seconds, 'secondText', 'secondsText', (value) => String(value).padStart(2, '0'));
      this.timer.textContent = parts.join(' ');
    }
  }

  customElements.define('me-agentur-delivery', MeAgenturDelivery);
}
